import React, {useEffect, useMemo} from 'react';
import SourceDestination from '../CreateRecommend/RecommendSourceDestination/SourceDestination';
import {RecommendPlanFormsStyled} from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {RecommendProps} from '../CreateRecommend/RecommendSourceDestination/validators';
import RecommendParserStep from '../RecommendParserStep/RecommendParserStep';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {withdrawalFieldsType} from './withdrawalFields';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {useResetReccs} from '../CreateRecommend/RecommendSourceDestination/useResetReccs';
import {
  changeWithdrawalDestinationFactory,
  changeWithdrawalSourceFactory,
  getWithdrawalMessageFactory,
  getWithdrawalSortedDestinations,
  withdrawalSources,
  withdrawalDestinations,
  loadSavedWithdrawal,
} from './WithdrawalHelpers';
import {WithdrawalSourcePlanForm} from './WithdrawalSourcePlanForm';
import {
  WithdrawalDestinationPlanForm,
  WithdrawalNewAccountPlanForm,
} from './WithdrawalDestinationPlanForm';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import {
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';

const Withdrawal = ({
  sourcePots,
  sourceDestinationLabels,
  destinationPots,
}: RecommendProps) => {
  const {loaded, setLoaded} = useLoadedAutoField();
  useResetReccs(OVRecommendActionEnum.WITHDRAW);
  const {recommendDetails, recommendReviewId, isSPPDA} = useRecommendContext();
  const [withdrawalFieldObj, withdrawMeta, withdrawHelpers] =
    useAutoField<recommendFieldsType['withdraw']>('withdraw');
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const [withdrawSourceObj, withdrawSourceMeta, withdrawSourceHelpers] = useAutoField<
    withdrawalFieldsType['source'] | undefined
  >('withdraw.source');
  const [withdrawDestinationObj, withdrawDestinationMeta, withdrawDestinationHelpers] =
    useAutoField<withdrawalFieldsType['destination'] | undefined>('withdraw.destination');
  const {value: withdraw} = withdrawalFieldObj;

  const getWithdrawalMessage = getWithdrawalMessageFactory({withdraw});
  const sources = useMemo(() => {
    const pots = withdrawalSources({
      sourcePots,
      isSppDa: isSPPDA,
      reviewId: recommendReviewId,
    });
    return pots;
  }, [sourcePots, isSPPDA, recommendReviewId]);
  const changeSource = changeWithdrawalSourceFactory({
    sourcePots,
    withdraw,
    withdrawSourceHelpers,
  });
  const changeDestination = changeWithdrawalDestinationFactory({
    withdraw,
    withdrawDestinationHelpers,
  });
  const destPots = useMemo(() => {
    const sortedDestinations = getWithdrawalSortedDestinations({
      sourcePots,
      destinationPots,
      withdrawSourceObj,
    });
    const pots = withdrawalDestinations({
      sortedDestinations,
      withdrawSourceObj,
    });
    return pots;
  }, [withdrawSourceObj.value?.id, sourcePots, destinationPots]);

  const withdrawalPot =
    sourcePots.find((x) => withdrawSourceObj.value?.id === x.id) || null;

  useEffect(() => {
    loadSavedWithdrawal({
      recommendDetails,
      withdrawHelpers,
    });
    setLoaded();
  }, [recommendDetails]);

  if (!values.mainPlanType) {
    return null;
  }
  return sourcePots?.length === 0 ? (
    <RecommendParserStep errorText="No accounts to withdraw from" />
  ) : (
    <SourceDestination
      fromError={withdrawSourceMeta.error}
      toError={withdrawDestinationMeta.error}
      source={
        <RecommendPlanFormsStyled>
          {sources.map((pot) => (
            <WithdrawalSourcePlanForm
              key={pot.id}
              pot={pot}
              getWithdrawalMessage={getWithdrawalMessage}
              withdrawSourceObj={withdrawSourceObj}
              withdrawSourceMeta={withdrawSourceMeta}
              handleChange={changeSource}
            />
          ))}
        </RecommendPlanFormsStyled>
      }
      destination={
        <RecommendPlanFormsStyled>
          {destPots.map((pot) => (
            <WithdrawalDestinationPlanForm
              key={pot.id}
              pot={pot}
              withdrawalFieldObj={withdrawalFieldObj}
              withdrawalPot={withdrawalPot}
              isSelected={withdrawDestinationObj.value?.id === pot.id}
              handleChange={changeDestination}
            />
          ))}
          <WithdrawalNewAccountPlanForm
            isSelected={withdrawDestinationObj.value?.id === NEW_PLAN_ID}
            handleChange={changeDestination}
          />
        </RecommendPlanFormsStyled>
      }
      sourceLabel={sourceDestinationLabels?.first}
      destinationLabel={sourceDestinationLabels?.second}
    />
  );
};

export default Withdrawal;
